import { z } from 'zod';

// Multi-level modifier nesting (Uber Eats modifier-of-modifier):
// Zod requires lazy() to handle recursive schemas.
// OrderModifierGroupSchema is defined lazily here to avoid a circular import.
const LazyOrderModifierGroupSchema: z.ZodTypeAny = z.lazy(() =>
    z.object({
        // 0 = UNRESOLVED (an inbound marketplace group we could not match by
        // id) — the same sentinel `OrderItem.menuItemId` uses. Never a list
        // position: see `UNRESOLVED_MENU_ID` in core's `menuIdLookup.ts`.
        modifierGroupId: z.number().int().nonnegative(),
        name: z.string().min(1),
        modifiers: z.array(OrderModifierSchema).default([]),
    }),
);

export const OrderModifierSchema: z.ZodTypeAny = z.object({
    // 0 = UNRESOLVED (an inbound marketplace option we could not match by id,
    // or a size rung — which carries `externalSizeKey` instead) — the same
    // sentinel `OrderItem.menuItemId` uses. Never a list position, which
    // borrowed an unrelated modifier's identity (`UNRESOLVED_MENU_ID`).
    modifierId: z.number().int().nonnegative(),
    modifierGuid: z.string().optional(),
    /**
     * Set INSTEAD of a catalog `modifierId` when this selection is a SIZE rung.
     *
     * 🔴 THE MODEL CARRIED THIS AND THE SCHEMA DID NOT, so Zod — which STRIPS
     * unknown keys rather than rejecting them — silently deleted it on every
     * write. The provider set it, ingest passed it through, and the persisted
     * modifier came back with `modifierId: 1` (the positional fallback) and no
     * size key at all. Two live orders showed exactly that while every unit test
     * passed, because the tests assert the provider's OUTPUT and never the
     * round trip through this schema.
     *
     * Holds the size's guid, or its name for a menu published before size guids
     * existed; `findSizeByExternalKey` resolves either.
     */
    externalSizeKey: z.string().optional(),
    name: z.string().min(1),
    priceDelta: z.number().default(0),
    // Selected instances of this modifier (e.g. "Extra Bacon ×2" → 2). Drives
    // RecipeOps modifier-draw scaling at order close. Defaults to 1.
    selectedQuantity: z.number().int().positive().optional().default(1),
    /**
     * Nested modifier groups (modifier-of-modifier). Absent for most items.
     * Populated from Uber Eats `selected_modifier_groups[].selected_items[]
     * .selected_modifier_groups[]` on recursive parse.
     */
    modifierGroups: z.array(LazyOrderModifierGroupSchema).optional(),
});

export const OrderModifierDataSchema: z.ZodTypeAny = z.object({
    // 0 = UNRESOLVED (an inbound marketplace option we could not match by id,
    // or a size rung — which carries `externalSizeKey` instead) — the same
    // sentinel `OrderItem.menuItemId` uses. Never a list position, which
    // borrowed an unrelated modifier's identity (`UNRESOLVED_MENU_ID`).
    modifierId: z.number().int().nonnegative(),
    modifierGuid: z.string().optional(),
    /**
     * Set INSTEAD of a catalog `modifierId` when this selection is a SIZE rung.
     *
     * 🔴 THE MODEL CARRIED THIS AND THE SCHEMA DID NOT, so Zod — which STRIPS
     * unknown keys rather than rejecting them — silently deleted it on every
     * write. The provider set it, ingest passed it through, and the persisted
     * modifier came back with `modifierId: 1` (the positional fallback) and no
     * size key at all. Two live orders showed exactly that while every unit test
     * passed, because the tests assert the provider's OUTPUT and never the
     * round trip through this schema.
     *
     * Holds the size's guid, or its name for a menu published before size guids
     * existed; `findSizeByExternalKey` resolves either.
     */
    externalSizeKey: z.string().optional(),
    name: z.string().min(1),
    priceDelta: z.number().optional().default(0),
    selectedQuantity: z.number().int().positive().optional().default(1),
    modifierGroups: z.array(LazyOrderModifierGroupSchema).optional(),
});

export type OrderModifierSchemaType = z.infer<typeof OrderModifierSchema>;
export type OrderModifierDataSchemaType = z.infer<typeof OrderModifierDataSchema>;
